// Deterministic per-job-type (JobTemplate) colors for the type lens: the same
// public_id always hashes to the same hue so a type reads consistently across
// the jobs list, the job page, and the planning board. Models the API shape on
// getKindColor (lib/assignments.ts) — a plain string → color function.

// ~10 accessible, visually distinct hues (readable as a small filled dot on both
// light and dark surfaces). Order is arbitrary; the hash spreads ids across them.
//
// ⚠ MIRRORED in Ruby: app/services/sectors/identity_color.rb renders the
// territory choropleth with these exact colors. Any palette or hash change
// must update both sides + regenerate
// spec/fixtures/files/identity_color_pairs.json (asserted by both suites) and
// bump the territory snapshot cache version (map_snapshot/territory/v1).
// Ten hues at one lightness, muted so a wall of discs reads as data and not
// as a toy; none sits beside the theme's own two accents (no amber next to
// the verb's peach, no cyan next to the state's teal). Ten entries so the
// hash keeps its modulus — a stored colour never moves.
export const TYPE_PALETTE = [
  '#3a6fd8', // blue
  '#2e9a5b', // green
  '#a5673f', // brown
  '#d2453f', // red
  '#8b5cd6', // violet
  '#4c7fb8', // steel
  '#d2498a', // pink
  '#7e9a22', // olive
  '#c9841a', // ochre
  '#5b5fd6', // indigo
] as const

// Neutral gray for the "Sans type" / null case (mirrors the muted technician
// color in KIND_COLORS).
export const NO_TYPE_COLOR = '#9ca3af'

// The swatches a dispatcher picks from when giving a type a CHOSEN color
// (JobTemplate#color). Deliberately SEPARATE from TYPE_PALETTE: that one is
// frozen because its hash output is mirrored in Ruby for the sector choropleth
// (see the ⚠ above) — a chosen color never touches that path, so this set can
// grow freely. Superset of TYPE_PALETTE (so "Auto" always matches an offered
// swatch) plus six more, each readable as a filled dot / stroked icon on both
// light and dark list surfaces.
export const SWATCH_PALETTE = [
  ...TYPE_PALETTE,
  '#0d9488', // teal
  '#0284c7', // sky
  '#7c3aed', // violet
  '#e11d48', // rose
  '#ca8a04', // yellow
  '#475569', // slate
] as const

// djb2 hash → palette index. Order-dependent (transposed suffixes don't
// collide) and stable per id, so a type keeps its color across screens and
// sessions regardless of catalog order.
export function getTypeColor(publicId?: string | null): string {
  if (!publicId) return NO_TYPE_COLOR
  let hash = 5381
  for (let i = 0; i < publicId.length; i++) {
    hash = (((hash << 5) + hash) + publicId.charCodeAt(i)) >>> 0
  }
  return TYPE_PALETTE[hash % TYPE_PALETTE.length]
}
